import { combineReducers } from 'redux';

import { EditorType } from '../constants';
import {
	paths,
	pages,
	previewPages,
	resources,
	parentFolderId,
	PathsState,
	PageDataState,
	ResourcesState,
	invoiceStatus,
	publishPrice,
	PublishPriceState,
} from './sources';
import { configs, ConfigsData } from './configs';
import { pagesController, PagesControllerState } from './pagesController';
import { thumbnails, ThumbnailsState } from './thumbnails';
import {
	type,
	label,
	isEditorLoaded,
	editPanel,
	activeItem,
	version,
	spreadZoom,
	lockedEditorPopup,
	LabelState,
	EditPanelState,
	ActiveItemState,
	LockedEditorPopupState,
	isAccessibleVersion,
} from './ui';
import {
	editableTextFields,
	editableMedia,
	editableLinks,
	editableButtons,
	editableIframes,
	editableEnrichments,
	editableSliders,
	themedItems,
	duplicatedItemsInSpread,
	EditableTextFieldsState,
	EditableMediaState,
	EditableLinksState,
	EditableButtonsState,
	EditableIframesState,
	EditableEnrichmentsState,
	EditableSlidersState,
	ThemedItemsState,
	DuplicatedItemsInSpreadState,
} from './editableItems';
import { pagesSelection, PagesSelectionState } from './pagesSelection';
import { themes, selectedThemeId, ThemesState } from './themes';
import { combo, ComboState } from './combo';
import { settings, SettingsState } from './settings';
import { preprodPublication, PreprodPublicationState } from './preprodPublication';
import { externalResources, ExternalResourcesState } from './externalResources';
import { spreadBackground, SpreadBackgroundState } from './spreadBackground';
import { undos, redos, HistoryState } from './history';
import { Api } from '../../../typings/api';

export interface EditorState {
	type: EditorType;
	parentFolderId: number | null;
	label: LabelState;
	pagesController: PagesControllerState;
	paths: PathsState;
	pages: PageDataState;
	previewPages: number[];
	resources: ResourcesState;
	configs: ConfigsData;
	editableTextFields: EditableTextFieldsState;
	editableMedia: EditableMediaState;
	editableLinks: EditableLinksState;
	editableButtons: EditableButtonsState;
	editableIframes: EditableIframesState;
	editableEnrichments: EditableEnrichmentsState;
	editableSliders: EditableSlidersState;
	themedItems: ThemedItemsState;
	duplicatedItemsInSpread: DuplicatedItemsInSpreadState;
	isEditorLoaded: boolean;
	editPanel: EditPanelState;
	thumbnails: ThumbnailsState;
	activeItem: ActiveItemState;
	combo: ComboState;
	version: number;
	pagesSelection: PagesSelectionState;
	spreadZoom: number;
	spreadBackground: SpreadBackgroundState;
	themes: ThemesState | null;
	selectedThemeId: string | null;
	settings: SettingsState;
	preprodPublication: PreprodPublicationState;
	externalResources: ExternalResourcesState;
	lockedEditorPopup: LockedEditorPopupState;
	history: HistoryState;
	invoiceStatus: Api.AssetItemInvoiceStatus;
	publishPrice: PublishPriceState;
	isAccessibleVersion: boolean;
}

export { PageData } from './sources';
export {
	ConfigsData,
	EditableDataState,
	BgStyleData,
	BackgroundMediaData,
	BackgroundImageOrMovieData,
	BackgroundSliderData,
	BackgroundMediaType,
	PositionConfig,
	Position,
} from './configs';
export {
	EditableTextFieldsState,
	EditableMediaState,
	EditableLinksState,
	EditableButtonsState,
	EditableIframesState,
	EditableEnrichmentsState,
	EditableSlidersState,
	ThemedItemsState,
	DuplicatedItemsInSpreadState,
} from './editableItems';
export { ThemesState, ThemeData } from './themes';

export const editor = combineReducers<EditorState>({
	type,
	parentFolderId,
	label,
	pagesController,
	paths,
	pages,
	previewPages,
	resources,
	configs,
	editableTextFields,
	editableMedia,
	editableLinks,
	editableButtons,
	editableIframes,
	editableEnrichments,
	editableSliders,
	themedItems,
	duplicatedItemsInSpread,
	isEditorLoaded,
	editPanel,
	thumbnails,
	activeItem,
	combo,
	version,
	pagesSelection,
	spreadZoom,
	spreadBackground,
	themes,
	selectedThemeId,
	settings,
	preprodPublication,
	externalResources,
	lockedEditorPopup,
	history: combineReducers<HistoryState>({
		undos,
		redos,
	}),
	invoiceStatus,
	publishPrice,
	isAccessibleVersion,
});



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/reducer/index.ts